import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { fetchContent, saveSettings } from "@/lib/api";
import { CONTENT_DEFAULTS } from "@/lib/useContent";

const FIELDS = [
    {
        key: "contact_email",
        label: "Contact email (used across the whole site)",
        hint: "Footer, forms and press page all point here.",
    },
    {
        key: "hero_intro",
        label: "Home hero intro paragraph",
        textarea: true,
    },
    {
        key: "host_bio_1",
        label: "About page, host bio, paragraph 1",
        textarea: true,
    },
    {
        key: "host_bio_2",
        label: "About page, host bio, paragraph 2",
        textarea: true,
    },
    {
        key: "host_bio_3",
        label: "About page, host bio, paragraph 3",
        textarea: true,
    },
];

const inputCls =
    "w-full bg-paper border-2 border-ink px-3.5 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:outline-none focus:ring-2 focus:ring-pink transition-shadow duration-150";

export default function ContentManager() {
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        fetchContent()
            .then((d) => setForm({ ...CONTENT_DEFAULTS, ...d.settings }))
            .catch(() => setForm({ ...CONTENT_DEFAULTS }));
    }, []);

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await saveSettings(form);
            toast.success("Site content updated. It's live on the pages now.");
        } catch {
            toast.error("Could not save. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    if (!form) return <p className="font-sub font-bold text-ink/60">Loading content…</p>;

    return (
        <form onSubmit={save} data-testid="content-manager-form" className="bg-cream border-2 border-ink hard-shadow p-6 sm:p-8 max-w-3xl">
            <h3 className="font-display font-black text-2xl text-ink">Edit the site's words</h3>
            <p className="mt-2 text-sm text-ink/70">
                Whatever you save here replaces the text on the public pages immediately.
            </p>
            <div className="mt-6 space-y-5">
                {FIELDS.map((f) => (
                    <label key={f.key} className="block">
                        <span className="font-mono-brand text-[10px] uppercase tracking-[0.18em] text-ink/60">
                            {f.label}
                        </span>
                        {f.textarea ? (
                            <textarea
                                data-testid={`content-field-${f.key}`}
                                className={`${inputCls} mt-1.5 min-h-[110px] resize-y`}
                                value={form[f.key] || ""}
                                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                            />
                        ) : (
                            <input
                                data-testid={`content-field-${f.key}`}
                                className={`${inputCls} mt-1.5`}
                                value={form[f.key] || ""}
                                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                            />
                        )}
                        {f.hint && <span className="block mt-1 text-xs text-ink/50">{f.hint}</span>}
                    </label>
                ))}
            </div>
            <button
                type="submit"
                data-testid="content-save-btn"
                disabled={busy}
                className="mt-7 inline-flex items-center gap-2 bg-ink text-paper font-sub font-bold px-7 py-3.5 border-2 border-ink hard-shadow-pink transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60"
            >
                <Save className="w-4 h-4" />
                {busy ? "Saving…" : "Save content"}
            </button>
        </form>
    );
}
